import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://www.crmonce.com/api';

export interface MeetingScheduleData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  meetingType: 'consultation' | 'demo' | 'discussion' | 'follow_up' | 'general';
  subject: string;
  description?: string;
  preferredDate?: string;
  preferredTime?: string;
  timezone?: string;
  duration?: number;
  serviceInterests?: string[];
  source?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}

export interface MeetingScheduleResponse {
  success: boolean;
  message: string;
  data?: {
    id: number;
    subject: string;
    meetingType: string;
    status: string;
  };
  error?: string;
}

export interface Meeting {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  meetingType: string;
  subject: string;
  description?: string;
  preferredDate?: string;
  preferredTime?: string;
  timezone: string;
  duration: number;
  serviceInterests: string[];
  source: string;
  status: string;
  priority: string;

  createdAt: string;
  updatedAt: string;
}

export interface MeetingStats {
  total: number;
  status: {
    pending: number;
    confirmed: number;
    completed: number;
  };

  meetingTypes: Array<{ meetingType: string; count: number }>;
  sources: Array<{ source: string; count: number }>;
}

class MeetingService {
  async scheduleMeeting(data: MeetingScheduleData): Promise<MeetingScheduleResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/meetings/schedule`, data);
      return response.data;
    } catch (error: any) {
      console.error('Error scheduling meeting:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to schedule meeting. Please try again.',
        error: error.response?.data?.error || error.message
      };
    }
  }

  async getMeetings(params?: {
    status?: string;
    meetingType?: string;
    source?: string;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data?: { meetings: Meeting[]; total: number; page: number; totalPages: number }; error?: string }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/meetings`, { params });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching meetings:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch meetings.'
      };
    }
  }

  async getMeeting(id: number): Promise<{ success: boolean; data?: Meeting; error?: string }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/meetings/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching meeting:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch meeting.'
      };
    }
  }

  async updateMeetingStatus(id: number, status: string): Promise<{ success: boolean; data?: Meeting; error?: string }> {
    try {
      const response = await axios.patch(`${API_BASE_URL}/meetings/${id}/status`, { status });
      return response.data;
    } catch (error: any) {
      console.error('Error updating meeting status:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update meeting status.'
      };
    }
  }



  async getMeetingStats(): Promise<{ success: boolean; data?: MeetingStats; error?: string }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/meetings/stats/overview`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching meeting statistics:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch meeting statistics.'
      };
    }
  }
}

export default new MeetingService();
